import { PropType, defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import { mapActions } from "pinia";
import { type CalendarOptions } from "@fullcalendar/core";
import moment from "moment";

import { EmployeeCalendarModel } from "../../../EmployeeCalendar/EmployeeCalendar.model";
import { ITaskByUserResponse } from "../../../../../services/task.service";
import {
    ApplicationEnum,
    ApplicationNameEnum,
    ParentPermissionEnum,
    PermissionActionEnum,
    EmployeeStatusEnum
} from "../../../../../enums/enums";
import { NavigationService } from "../../../../../services/navigation.service";
import { PermissionManager } from "../../../../../common/utilities/permission-manager";
import { EmployeeCalendarContent } from "../../../../../pages/central-calendar/employee-calendar-content";
import { formatName } from "../../../../../common/format-name";
import { GetProductClass } from "../../../../../resources/value-converters/get-product-class";
import { FormatPhoneNumber } from "../../../../../resources/value-converters/format-phone";
import { ScheduleLegendState } from "../../../../../pages/central-calendar/central-calendar";
import config from "../../../../../common/config";
import { BulkReassign } from "../../../../../resources-vue/vue-models/bulk-reassign";
import { useLoadingIndicator } from "../../../../Common/LoadingIndicator/store/useLoadingIndicator";
import { formatScheduledTime } from "../../../../../common/vue-helpers/modifiers/value-modifier";
import { getHospiceRedirectionUrl } from "../../../../../common/get-hospice-redirection-url";
import { PayType } from "../../../../../enums/PayTypeEnum";
import { AnalyticsCategory, AnalyticsService } from "../../../../../services/analytics.service";
import { isMobileScreenSize } from "../../../../../utilities/isMobileScreenSize";

import FullCalendar from "../../../../../resources-vue/vue-elements/FullCalendar/FullCalendar.vue";

export const nonEvvVisitStatus = {
    notYetStarted: ["not yet due", "not yet started"],
    inProgress: ["saved", "saved from mobile device"],
    completed: [
        "completed",
        "completed (pending qa review)",
        "submitted",
        "submitted pending co-signature",
        "submitted with signature",
        "sent to physician (electronically)",
        "to be sent to physician",
        "exported",
        "export ready",
        "not exported",
        "sent to physician",
        "missed visit",
        "missed visit(complete)",
        "missed visit(pending)",
        "missed visit(return)",
        "returned w/ physician signature",
        "returned for clinician signature",
        "returned w/ physician signature",
        "returned for review",
        "reopened"
    ]
};

export default defineComponent({
    props: {
        startDate: { type: Date },
        endDate: { type: Date },
        usersList: { type: Array as PropType<EmployeeCalendarModel[]>, default: [] },
        tasksList: { type: Array, default: [] },
        tasksMap: { default: null },
        onCalendarRefresh: { type: Function },
        toggleFindNearByClientsView: { type: Function },
        isPremiumByAgency: {
            type: Object,
            default: null
        }
    },
    components: {
        FullCalendar
    },

    data(vm) {
        return {
            _analyticsService: null as AnalyticsService,
            isLoading: false as boolean,
            _navigationService: null,
            _permissionManager: null,
            _taskService: null,
            _toastrService: null,
            _branchService: null,
            employeeCalendarContent: null,
            dialogRef: null,
            editable: true,
            eventLimit: 4 as number,
            userNamesMap: null,
            employeeStatuses: Object.create(EmployeeStatusEnum),
            PayType: PayType,
            isMobile: false as boolean
        };
    },
    created() {
        this._navigationService = Container.instance.get(NavigationService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this._analyticsService = Container.instance.get(AnalyticsService);
        this.employeeCalendarContent = Container.instance.get(EmployeeCalendarContent);
        this.isMobile = isMobileScreenSize();
    },
    computed: {
        calendarOptions(): CalendarOptions {
            return {
                resources: this.userDetails,
                resourceOrder: "LastName, FirstName MiddleInitial",
                resourceGroupField: "groupId",
                resourceAreaWidth: this.isMobile ? "600px" : "100%",
                contentHeight: "auto",
                resourceAreaColumns: [
                    this.isMobile
                        ? {
                              headerContent: "Employee Name",
                              width: 150
                          }
                        : {
                              headerContent: "Employee Name"
                          },
                    {
                        headerContent: "Not Yet Started",
                        cellContent: (arg) => {
                            const uniqueId = `cell-notYetStarted-${arg.resource.id}`;
                            const html = `<div id="${uniqueId}" class="cell-container">${this.generateEvent(
                                arg.resource.extendedProps.tasks.notYetStarted,
                                arg.resource.extendedProps
                            )}</div>`;
                            this.$nextTick(() => {
                                const el = document.getElementById(uniqueId);
                                this.mountVisitTooltip({ el }, arg.resource.extendedProps.tasks.notYetStarted);
                            });
                            return {
                                html
                            };
                        }
                    },
                    {
                        headerContent: "In Progress",
                        cellContent: (arg) => {
                            const uniqueId = `cell-inProgress-${arg.resource.id}`;
                            const html = `<div id="${uniqueId}" class="cell-container">${this.generateEvent(
                                arg.resource.extendedProps.tasks.inProgress,
                                arg.resource.extendedProps
                            )}</div>`;
                            this.$nextTick(() => {
                                const el = document.getElementById(uniqueId);
                                this.mountVisitTooltip({ el }, arg.resource.extendedProps.tasks.inProgress);
                            });
                            return {
                                html
                            };
                        }
                    },
                    {
                        headerContent: "Completed",
                        cellContent: (arg) => {
                            const uniqueId = `cell-completed-${arg.resource.id}`;
                            const html = `<div id="${uniqueId}" class="cell-container">${this.generateEvent(
                                arg.resource.extendedProps.tasks.completed,
                                arg.resource.extendedProps
                            )}</div>`;
                            this.$nextTick(() => {
                                const el = document.getElementById(uniqueId);
                                this.mountVisitTooltip({ el }, arg.resource.extendedProps.tasks.completed);
                            });
                            return {
                                html
                            };
                        }
                    }
                ],
                // NOTE: Uncomment when task navigation is needed
                // eventClick: (event: any) => {
                //     if (!$(event.el).find(".more-btn").length) {
                //         this.handleEventClick(event);
                //     }
                // },
                resourceLabelContent: (arg: any) => {
                    return {
                        html: this.generateResource(arg)
                    };
                },
                resourceLabelDidMount: (arg: any) => {
                    this.registerClick(arg);
                },
                resourceGroupLabelContent: (arg: any) => {
                    return {
                        html: this.generateGroupLabel(arg)
                    };
                }
            };
        },

        userDetails() {
            let userEntries: any[] = [];
            this.userNamesMap = new Map();
            this.usersList?.forEach((user) => {
                user.Agencies.forEach((op) => {
                    let tasks = {
                        notYetStarted: [] as ITaskByUserResponse[],
                        inProgress: [] as ITaskByUserResponse[],
                        completed: [] as ITaskByUserResponse[]
                    };
                    let userData = {
                        ...user,
                        ...op,
                        groupId: user.Id,
                        id: `${op.UserId}${op.Application}${op.AgencyId}`,
                        tasks: tasks
                    };
                    this.tasksList.forEach((task: any) => {
                        if (task.UserId === op.UserId) {
                            if ([ApplicationEnum.AgencyCore, ApplicationEnum.HomeCare].includes(task.Application)) {
                                if (task.EvvStatus && task.EvvStatus !== "0") {
                                    if (["InProgress", "1", "3"].includes(task.EvvStatus)) {
                                        userData.tasks.inProgress.push(task);
                                    } else if (["Finished", "2", "4"].includes(task.EvvStatus)) {
                                        userData.tasks.completed.push(task);
                                    }
                                } else {
                                    this.addTask(task, userData);
                                }
                            } else {
                                this.addTask(task, userData);
                            }
                        }
                    });

                    userEntries.push(userData);
                });
                this.userNamesMap.set(
                    user.Id,
                    formatName(user.FirstName, user.LastName, "LN, FN", "", user.MiddleInitial)
                );
            });
            return userEntries ?? [];
        }
    },
    methods: {
        ...mapActions(useLoadingIndicator, { showLoading: "SET_LOADING" }),
        generateGroupLabel(arg: any) {
            const groupId = arg?.groupValue;
            const employeeName = this.userNamesMap?.get(groupId);
            let labelContent = "";
            labelContent = `<span class="text-dark fw-bold"> ${employeeName} </span>`;
            return labelContent;
        },
        getStatusText(status: number): string {
            const result = this.employeeStatuses[status].toLowerCase();

            if (result.includes("pending")) {
                return "Pending";
            } else if (result.includes("terminate")) {
                return "Terminated";
            }
            return result;
        },
        generateResource(arg: any) {
            const resource = arg?.resource?.extendedProps;
            let userInfo = "";
            let userName = `<span class="fw-bold"> ${formatName(
                resource.FirstName,
                resource.LastName,
                "LN, FN",
                "",
                resource.MiddleInitial
            )} </span>`;
            const phoneNumber = new FormatPhoneNumber().toView(resource.PhoneNumber?.toString());

            const hasSchedulerViewPermissionHomeCare = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.scheduler,
                PermissionActionEnum.canAdd,
                ApplicationEnum.HomeCare
            );
            const hasSchedulerViewPermissionHos = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.scheduler,
                PermissionActionEnum.canAdd,
                ApplicationEnum.AxxessHospiceFE
            );
            const hasSchedulerViewPermissionPal = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.scheduler,
                PermissionActionEnum.canAdd,
                ApplicationEnum.AxxessPalliative
            );
            const hasSchedulerViewPermissionHomeHealth = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.scheduler,
                PermissionActionEnum.canAdd,
                ApplicationEnum.AgencyCore
            );

            if (!!resource) {
                const productClass = new GetProductClass();
                let application = resource.Application;
                if (application === 256) {
                    application = ApplicationEnum.AxxessHospice;
                }
                if (application !== ApplicationEnum.AgencyCore) {
                    userName = `<a class="w-100 font-size-base text-truncate cursor-pointer user-link" id="${
                        resource.Id
                    }">
                            ${formatName(resource.FirstName, resource.LastName, "LN, FN", "", resource.MiddleInitial)}
                        </a>`;
                }
                userInfo = `<div class="mx-2 d-flex flex-column align-items-start p-2">
                    <span class="fw-normal font-size-xs badge badge-pill pill me-1 ${productClass.toView(application)}">
                        ${ApplicationNameEnum[application]}
                    </span>
                    <div class="position-absolute bulk-reassign-wrapper">
                        <button
                            type="button"
                            class="btn font-size-xs px-1 py-0 btn-outline-primary"
                            data-bs-toggle="dropdown"
                            id="reassign-ellipsis-${resource.Id}"
                        >
                            <i class="fas fa-ellipsis-h"></i>
                        </button>
                        <div class="dropdown-menu py-1" aria-labelledby="reassign-ellipsis-${resource.Id}">
                            <a
                                class="dropdown-item font-size-sm bulk-reassign"
                                href="javascript:void(0);"
                                id="task-manager-${resource.Id}"
                            >
                                Task Manager
                            </a>
                            ${
                                (application == ApplicationEnum.AxxessHospiceFE && hasSchedulerViewPermissionHos) ||
                                (application == ApplicationEnum.AxxessHospice && hasSchedulerViewPermissionHos) ||
                                (application == ApplicationEnum.AxxessPalliative && hasSchedulerViewPermissionPal) ||
                                (application == ApplicationEnum.HomeCare && hasSchedulerViewPermissionHomeCare) ||
                                (application == ApplicationEnum.AgencyCore && hasSchedulerViewPermissionHomeHealth)
                                    ? this.isPremiumByAgency?.[resource.AgencyId]
                                        ? `<a class="dropdown-item font-size-sm bulk-reassign border-top near-ai-button" href="javascript:void(0);" id="find-near-by-patients-${resource.UserId}-${resource.AgencyId}">Find Nearby Patients</a>`
                                        : `<div class="dropdown-item font-size-sm border-top near-ai-button ai-disabled" id="find-near-by-patients-${resource.UserId}-${resource.AgencyId}">Find Nearby Patients <img src="${require("../../../../../assets/images/lock.svg")}" />
                                            <span class="locked-premium-tooltip">Contact Axxess to unlock this <br /> premium Axxess intelligence feature.</span></div>`
                                    : ""
                            }

                        </div>
                    </div>
                    <p class="mb-0 w-auto user-name mt-2 mt-md-0">
                        ${userName}
                        <span class="font-size-xs">${resource.Credentials}</span>
                    </p>
                    ${
                        phoneNumber
                            ? `<a href="tel:${phoneNumber}" class="mb-0 font-size-xs w-auto">${phoneNumber}</a>`
                            : ""
                    }
                    <p class="w-100 font-size-2xs text-truncate mb-0">${resource.AgencyName}</p>
                    <p
                                class="fw-bold status-label mb-0 font-size-2xs ${this.getStatusText(
                                    resource.Status
                                ).toLowerCase()}"
                            >
                                Status : <span class="text-capitalize"> ${this.getStatusText(resource.Status)} </span>
                            </p>
                             <p class="fw-bold status-label mb-0 font-size-2xs">PayType:<span class="text-capitalize fw-normal"> ${this.PayType.getKeyUsingValue(
                                 resource.PayType
                             )} </span></p>
                </div>`;
            }
            return userInfo;
        },
        generateEvent(events: any, userData?: any) {
            let eventContent = "";
            if (!events) {
                return eventContent;
            }

            events?.forEach((task: ITaskByUserResponse) => {
                let eventStatus = "";
                let eventClass = "";
                if (task.VisitConfirmed) {
                    eventStatus = `<i class="fa fa-check-circle" aria-hidden="true"></i> Confirmed`;
                    eventClass = "confirmed";
                } else if (task.PatientCalled) {
                    eventStatus = `<i class="fa fa-exclamation-circle" aria-hidden="true"></i> Reschedule`;
                    eventClass = "reschedule"; // using 'CalledPatient' temporary until we get veriable for "Call To Reschedule" from Solution
                }
                let isEventstatus = false;
                if (task.VisitConfirmed || task.PatientCalled) {
                    isEventstatus = true;
                }
                const isNonPatientVisit = !task.PatientId;
                let patientName = "";
                if (!isNonPatientVisit) {
                    patientName = `<span class="d-flex align-items-center font-size-3xs fw-semibold padding-top">
                        <i class="fas fa-user me-1"></i>
                        <span>
                            ${this.getUserName(task.PatientFirstName, task.PatientLastName)}
                        </span>
                    </span>`;
                }
                eventContent +=
                    userData.AgencyId == task.AgencyId
                        ? `<div class="ax-calendar-event mb-2 ${ScheduleLegendState[task.State]} " data-taskid="${
                              task.Id
                          }">
                    <span class="w-100 fw-bold text-truncate font-size-xs">
                        ${task.TaskName}
                    </span>
                      <span class="w-100 font-size-3xs text-truncate padding-top">
                        ${task.Status}
                    </span>
                    <span class="w-100 font-size-3xs text-truncate">
                        ${this.getTimeInOut(
                            task.State,
                            formatScheduledTime(task.TimeIn),
                            formatScheduledTime(task.TimeOut),
                            formatScheduledTime(task.StartTime),
                            formatScheduledTime(task.EndTime)
                        )}
                    </span>
                    ${patientName}
                    <span class="d-flex w-100 justify-content-between align-items-center ${
                        isEventstatus || task?.OnCall || task.PRN ? `padding-top` : ``
                    }">
                        ${
                            isEventstatus
                                ? `<span class="status-${eventClass} fw-semibold">
                                    ${eventStatus}
                                </span>`
                                : ``
                        }
                        ${task?.OnCall ? `<span class="pill pill-oncall"> On-Call </span>` : ``}
                        ${task.PRN ? `<span class="pill pill-prn"> PRN </span>` : ``}
                    </span>
                </div>`
                        : "";
            });
            return eventContent;
        },
        addTask(task: ITaskByUserResponse, userData: any) {
            const taskStatus = task.Status.toLowerCase();
            if (nonEvvVisitStatus.notYetStarted.includes(taskStatus)) {
                userData.tasks.notYetStarted.push(task);
            } else if (nonEvvVisitStatus.inProgress.includes(taskStatus)) {
                userData.tasks.inProgress.push(task);
            } else {
                userData.tasks.completed.push(task);
            }
        },
        registerClick(arg: any) {
            const element = arg?.el?.className.split(" ").join(".");
            const extendedProps = arg?.resource?.extendedProps;
            $(`.${element}`).on("click", `#${extendedProps?.Id}`, (event: any) => {
                this.handleUserNameClick(extendedProps);
            });
            $(`.${element}`).on("click", `#task-manager-${extendedProps?.Id}`, (event: any) => {
                this.openTaskManager(extendedProps);
            });
            $(`.${element}`).on(
                "click",
                `#find-near-by-patients-${extendedProps?.UserId}-${extendedProps?.AgencyId}`,
                (event: any) => {
                    if ($(event.currentTarget).hasClass("ai-disabled")) {
                        return;
                    }
                    this.toggleFindNearByClientsView(extendedProps);
                }
            );
        },
        async openTaskManager(employee: BulkReassign) {
            this._analyticsService.logEvent({
                category: AnalyticsCategory.CentralVisitActions,
                action: "Employee Daily Task Manager"
            });
            let application;
            if (employee.Application === ApplicationEnum.AxxessHospice) {
                application = ApplicationEnum.AxxessHospiceFE;
            } else {
                application = employee.Application;
            }
            this.$router.push({
                path: "/axxess-central/task-manager?",
                query: {
                    agencyId: employee.AgencyId,
                    fullname: `${employee.FirstName} ${employee.LastName} ${employee.Credentials}`,
                    application: application,
                    userId: employee.UserId
                }
            });
        },
        async handleUserNameClick(resource: any) {
            let redirectResponse;
            let action = "";
            if (resource.Application === ApplicationEnum.AxxessHospice) {
                redirectResponse = {
                    redirectUrl: `${getHospiceRedirectionUrl(resource.AgencyId)}/employees/employee-center/${
                        resource.UserId
                    }?accountId=${resource.AgencyId}`,
                    isSuccessful: true
                };
                action = "AxxessHospice";
            } else if (resource.Application === ApplicationEnum.AxxessPalliative) {
                redirectResponse = {
                    redirectUrl: `${config.palliativeUrl}/employees/employee-center/${resource.UserId}?accountId=${resource.AgencyId}`,
                    isSuccessful: true
                };
                action = "AxxessPalliative";
            } else {
                const isClinician = this._permissionManager.checkPermission(
                    ParentPermissionEnum.clinician,
                    PermissionActionEnum.canView
                );
                this.showLoading(true);
                redirectResponse = await this._navigationService.navigateToUser({
                    agencyId: resource.AgencyId,
                    applicationId: resource.Application,
                    isClinician: isClinician,
                    userId: resource.UserId
                });
                action = resource.Application === ApplicationEnum.AgencyCore ? "AxxessHomeHealth" : "AxxessHomeCare";
                this.showLoading(false);
            }
            this._navigationService.redirect(redirectResponse, {
                category: AnalyticsCategory.CentralCalendarNavigation,
                action
            });
        },
        mountVisitTooltip(arg: any, tasksArray: any) {
            $('[role="tooltip"]')?.remove();
            if (!arg?.el) {
                return;
            }
            let visitElements = $(arg?.el).find(".ax-calendar-event");
            visitElements.each((i, el) => {
                let currentTaskId = $(el).data("taskid");
                if (tasksArray && tasksArray.length) {
                    tasksArray?.forEach((task: any) => {
                        if (task.Id === currentTaskId) {
                            if ($(el).hasClass("non-visit-bg")) {
                                this.attachNonVisitTooltip(el, task);
                                return;
                            }
                            this.attachTooltip(el, task);
                        }
                    });
                }
            });
        },
        attachTooltip(el: any, arg: any) {
            const element = $(el);
            const content = this.getTooltipContent(arg);
            (element as any).popover({
                html: true,
                content: content,
                trigger: "hover",
                placement: "auto",
                container: "body"
            });
        },
        getTooltipContent(data: any) {
            let content = "";
            let evvContent = "";
            if (this.getEvvContent(data).length > 0 && this.getEvvContent(data) != "Not Yet Started") {
                evvContent = `<span>
                                <strong>EVV Time:</strong>
                                ${this.getEvvContent(data)}
                            </span>`;
            }
            if (!!data) {
                content = `<div class="d-flex flex-column align-items-start event-tooltip font-size-xs">
                                <span><strong>Branch:</strong> ${data.BranchName}</span>
                                ${data.Payer ? `<span><strong>Payer:</strong> ${data.Payer}</span>` : ``}
                                <span><strong>Billable:</strong> ${!!data.IsBillable ? "Yes" : "No"}</span>
                                <span><strong>Payable:</strong> ${!!data.IsPayable ? "Yes" : "No"}</span>
                                ${
                                    data.ScheduledByFirstName
                                        ? `<span>
                                        <strong>Scheduled By:</strong>
                                            ${this.getUserName(data.ScheduledByFirstName, data.ScheduledByLastName)}
                                        </span>`
                                        : ``
                                }
                                ${evvContent}
                            <div>`;
            }
            return content;
        },
        attachNonVisitTooltip(el: any, arg: any) {
            const element = $(el);
            const content = this.getNonVisitTooltipContent(arg);
            (element as any).popover({
                html: true,
                content: content,
                trigger: "hover",
                placement: "auto",
                container: "body"
            });
        },
        getNonVisitTooltipContent(data: any) {
            let content = "";
            if (!!data) {
                content = `<div class="d-flex flex-column align-items-start event-tooltip font-size-xs">
                                <span><strong>Mileage:</strong> ${data.Mileage || "Not Available"}</span>
                            <div>`;
            }
            return content;
        },
        getEvvContent(data: any) {
            let evvTime = "";
            if (data.Application === ApplicationEnum.AxxessHospiceFE) {
                return evvTime;
            } else if (!data.EvvTimeIn && !data.EvvTimeOut) {
                evvTime = "Not Yet Started";
            } else if (data.Status === "Completed") {
                const evvTimeIn = data.EvvTimeIn ? data.EvvTimeIn : "Not Collected";
                const evvTimeOut = data.EvvTimeIn ? data.EvvTimeOut : "Not Collected";
                evvTime = this.employeeCalendarContent.formatEVVTime(evvTimeIn, evvTimeOut);
            } else {
                const evvTimeIn = data.EvvTimeIn || "Not Collected";
                const evvTimeOut = data.EvvTimeOut || "Not Collected";
                evvTime = this.employeeCalendarContent.formatEVVTime(evvTimeIn, evvTimeOut);
            }
            return evvTime;
        },
        getUserName(firstName: string, lastName: string) {
            let userName = "";
            if (!firstName && !lastName) {
                userName = "Unassigned";
            } else {
                userName = formatName(firstName, lastName, "FN LN");
            }
            return userName;
        },
        getTimeInOut(state: number, timeIn: string, timeOut: string, startTime: string, endTime: string) {
            if (state != 4 && !startTime && !endTime) {
                return "";
            }
            if (state != 4) {
                return `${startTime || ""} - ${endTime || ""}`;
            }
            if (!timeIn && !timeOut) {
                return "";
            }
            return `${timeIn || ""} - ${timeOut || ""}`;
        }
    }
});
